import { difference, intersection } from 'lodash';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { SettingsReader } from '../components/PackagePicker/DefaultSettingsAdapter';
import { WidgetPreset } from '../components/PackagePicker/settingsParams';
import { DEFAULT_PLAN_LIMIT } from '../constants';
import { isTruthy } from './isTruthy';

type AnyPlan = PublicPlan | Plan;

interface VisibilityFilter {
  filter: 'hidden' | 'visible';
  ids: string[];
}

export function sortPlansByVisibility(allPlans: AnyPlan[], { filter, ids }: VisibilityFilter) {
  return allPlans.slice().sort((planA, planB) => {
    if (ids.includes(planA.id!) && !ids.includes(planB.id!)) {
      return filter === 'visible' ? -1 : 1;
    }
    if (ids.includes(planB.id!) && !ids.includes(planA.id!)) {
      return filter === 'visible' ? 1 : -1;
    }
    return 0;
  });
}

export function getOrderedVisiblePlans(plans: AnyPlan[], settings: SettingsReader) {
  if (settings.get('preset') === WidgetPreset.SINGLE_PLAN) {
    return plans.slice(0, 1);
  }
  const orderedPlans = getOrderedPlans(plans, settings);
  return getVisiblePlans(orderedPlans, settings);
}

export function getHiddenPlanIds(plans: AnyPlan[], settings: SettingsReader): string[] {
  const ids = plans.map((p) => p.id).filter((id): id is string => !!id);
  return shouldUseVisiblePlansParam(settings)
    ? difference(ids, settings.uuidAsArray('visiblePlans'))
    : intersection(ids, settings.uuidAsArray('hiddenPlans'));
}

export function shouldUseVisiblePlansParam(settings: SettingsReader) {
  return settings.get('useVisiblePlans') || settings.uuidAsArray('visiblePlans').length > 0;
}

export function getOrderedPlans(plans: AnyPlan[], settings: SettingsReader): AnyPlan[] {
  const planOrder = settings.uuidAsArray('planOrder');
  const ordered = planOrder.map((id) => plans.find((p) => p.id === id)).filter(isTruthy);
  const unordered = plans.filter((p) => !planOrder.includes(p.id ?? ''));
  return [...ordered, ...unordered];
}

function getVisiblePlans(plans: AnyPlan[], settings: SettingsReader): AnyPlan[] {
  const hiddenPlanIds = getHiddenPlanIds(plans, settings);
  return plans.filter((p) => !hiddenPlanIds.includes(p.id ?? '')).slice(0, DEFAULT_PLAN_LIMIT);
}
